import React, {useEffect, useRef, useState} from 'react';
import * as d3 from 'd3';
import {createRoot} from 'react-dom/client';
import LinkTooltip from './LinkTooltip';
import ArrTooltip from '../../../../components/common/arr-tooltip/ArrTooltip';
import InnerTextFrame from './InnerTextFrame';
import BubbleTooltip from './BubbleTooltip';
import {ExtendedILink, ExtendedINode, ILink, INode} from './type';
import {Box} from '@mui/material';

const colors = {
  nodes_default: '#4555D6',
  links_default: '#B2BFD3',
  hover: '#6E87FF',
  clicked: '#9F75DF',
  box_default: '#F1F7FF',
};

interface ILinkAndBubble {
  data: {link: ILink[]; node: INode[]};
  svgRef: React.RefObject<SVGSVGElement | null>;
  onNodeSelectionChange?: (selectedNodeIds: number[]) => void;
  onLinkSelectionChange?: (selectedLinkIds: string[]) => void;
  selectedNodeIds?: Set<number>;
  selectedLinkIds?: Set<string>;
}

// ===================== LinkAndBubble Component =====================
const LinkAndBubble = ({
  data,
  svgRef,
  onNodeSelectionChange,
  onLinkSelectionChange,
  selectedNodeIds,
  selectedLinkIds,
}: ILinkAndBubble) => {
  const [size, setSize] = React.useState<DOMRect | null>(null);

  useEffect(() => {
    const handleResize = () => {
      const doc = document.getElementsByClassName(
        'bubbles-container'
      )[0] as HTMLElement;
      setSize(doc.getBoundingClientRect() ?? null);
    };
    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  const nodeLabelRootsRef = useRef(new Map());
  const [tooltip, setTooltip] = useState<{
    visible: boolean;
    content: {
      type: string;
      link: ExtendedILink | null;
      node: ExtendedINode | null;
    };
    x: number;
    y: number;
  }>({
    visible: false,
    content: {type: '', link: null, node: null},
    x: 0,
    y: 0,
  });
  const MARGIN = {
    top: 20,
    left: 40,
    right: 25,
    bottom: 110,
  };
  useEffect(() => {
    if (!size) return;
    const svg = d3.select(svgRef.current);

    //preserve node position before cleaning
    const existingNodes: ExtendedINode[] = svg
      .selectAll('circle, rect')
      .data() as ExtendedINode[];
    const positionMap = new Map();
    existingNodes.map((d) => {
      if (d.x && d.y) {
        positionMap.set(d.id, {x: d.x, y: d.y, fx: d.fx, fy: d.fy});
      }
    });

    svg.selectAll('*').remove();

    //deep clone data
    const nodes: ExtendedINode[] = JSON.parse(JSON.stringify(data.node));
    const allLinks = JSON.parse(JSON.stringify(data.link));

    const max = nodes.reduce((ac: number, item: INode) => {
      return Math.max(ac, item.exchangeCount);
    }, 0);
    const links: ExtendedILink[] = allLinks.map((item: ILink) => ({
      source: item.fromNodeID,
      target: item.toNodeID,
      value: item.connectorExchangeCount,
      label: 'My Label',
    }));
    //restore position
    nodes.forEach((node, i) => {
      const savedPos = positionMap.get(node.id);
      const ratio = max ? node.exchangeCount / max : 0;
      const radiusNumber = Number(ratio.toFixed(2)) * 100;
      if (size.height > 720) {
        node.radius = radiusNumber > 45 ? radiusNumber : 45;
      } else if (size.height > 400) {
        node.radius = radiusNumber > 26 ? radiusNumber : 26;
      } else if (size.height > 300) {
        node.radius = radiusNumber > 25 ? radiusNumber : 25;
      } else {
        node.radius = radiusNumber > 22 ? radiusNumber : 22;
      }
      // Update width and height proportionally
      node.x = node.radius * 2;
      node.y = node.radius * 2;
      // For visualization, set height and width proportional to radius
      node.index = i;
      if (savedPos) {
        node.x = savedPos.x;
        node.y = savedPos.y;
        node.fx = savedPos.fx;
        node.fy = savedPos.fy;
      }
    });

    //force simulation setup
    const simulation = d3
      .forceSimulation(nodes)
      .force(
        'link',
        d3
          .forceLink<ExtendedINode, d3.SimulationLinkDatum<ExtendedINode>>(
            links
          )
          .id((d) => d.id)
          .distance(40)
      )
      .force('charge', d3.forceManyBody().strength(-25))
      .force(
        'center',
        d3.forceCenter(
          (size.width - MARGIN.left - MARGIN.right) / 2 + MARGIN.left,
          (size.height - MARGIN.top - MARGIN.bottom) / 2 + MARGIN.top
        )
      )
      .force(
        'collision',
        d3.forceCollide<ExtendedINode>().radius((d) => d.radius * 1.8)
      );

    //helper fn to get link id

    const getLinkId = (d: ExtendedILink) => {
      if (!d) {
        return '';
      }
      const sourceId = typeof d.source === 'object' ? d.source.id : d.source;
      const targetId = typeof d.target === 'object' ? d.target.id : d.target;
      return `${sourceId}-${targetId}`;
    };

    //drawing links

    const link = svg
      .append('g')
      .selectAll('line')
      .data<ExtendedILink>(links)
      .join('line')
      .attr('stroke-width', (d: ExtendedILink) => Math.sqrt(d.value))
      .attr('stroke', (d: ExtendedILink) => {
        const linkId = getLinkId(d);
        return selectedLinkIds?.size && selectedLinkIds.has(linkId)
          ? colors.clicked
          : colors.links_default;
      })
      .on(
        'mouseover',
        (event: React.MouseEvent<HTMLElement>, d: ExtendedILink) => {
          const linkId = getLinkId(d);
          d3.select(event.currentTarget).attr(
            'stroke',
            selectedLinkIds?.size && selectedLinkIds.has(linkId)
              ? colors.clicked
              : colors.hover
          );
          setTooltip({
            visible: true,
            content: {type: 'link', link: d, node: null},
            x: event.clientX,
            y: event.clientY - 10,
          });
        }
      )
      .on('mouseout', (event: React.MouseEvent<HTMLElement>, d) => {
        const linkId = getLinkId(d);
        d3.select(event.currentTarget).attr(
          'stroke',
          selectedLinkIds?.size && selectedLinkIds.has(linkId)
            ? colors.clicked
            : colors.links_default
        );
        setTooltip({
          visible: false,
          content: {type: '', link: null, node: null},
          x: 0,
          y: 0,
        });
      })
      .on('click', (event: React.MouseEvent<HTMLElement>, d) => {
        event.stopPropagation();
        const linkId = getLinkId(d);
        const newSet = new Set(selectedLinkIds);
        if (newSet.has(linkId)) {
          newSet.delete(linkId);
          d3.select(event.currentTarget).attr('stroke', colors.hover);
        } else {
          newSet.add(linkId);
          d3.select(event.currentTarget).attr('stroke', colors.clicked);
        }
        onLinkSelectionChange && onLinkSelectionChange(Array.from(newSet));
      });

    //Drawing nodes
    const nodeGroup = svg
      .append('g')
      .selectAll('g')
      .data(nodes)
      .join('g')
      .attr('tabindex', 0)
      .attr('role', 'button')
      .attr('tabIndex', 0)
      .on('keydown', (event, d) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.stopPropagation();
          onNodeSelectionChange?.([d.id]);
        }
      })
      .call(drag(simulation));

    nodeGroup
      .append(function (d) {
        const shape = d.name === 'OTHERS' ? 'rect' : 'circle';
        return document.createElementNS(d3.namespaces.svg, shape);
      })
      .attr('data-testid', (d) => {
        return 'btn-result-node-' + d.index;
      })
      .attr('fill', (d) =>
        selectedNodeIds?.size && selectedNodeIds.has(d.id)
          ? colors.clicked
          : d.name === 'OTHERS'
            ? colors.box_default
            : colors.nodes_default
      )
      .each(function (d) {
        if (d.name === 'OTHERS') {
          d3.select(this)
            .attr('width', 320)
            .attr('height', 108)
            .attr('x', -d.radius)
            .attr('y', -d.radius);
        } else {
          d3.select(this).attr('r', d.radius);
        }
      })
      .on('mouseover', (event: MouseEvent, d: ExtendedINode) => {
        const currentNode = event.currentTarget as SVGElement;
        const parentGElement = currentNode.parentNode as SVGElement;
        const group = d3.select(parentGElement);
        group
          .select('circle')
          .attr(
            'fill',
            selectedNodeIds?.size && selectedNodeIds.has(d.id)
              ? colors.clicked
              : colors.hover
          );
        group
          .select('rect')
          .attr(
            'fill',
            selectedNodeIds?.size && selectedNodeIds.has(d.id)
              ? colors.clicked
              : colors.hover
          );
        setTooltip({
          visible: true,
          content: {type: 'node', node: d, link: null},
          x: event.clientX,
          y: event.clientY - 10,
        });
        link.attr('stroke', (l: any) => {
          if (l.source.id === d.id || l.target.id === d.id) {
            return colors.hover;
          } else {
            return selectedLinkIds?.size && selectedLinkIds.has(getLinkId(l))
              ? colors.clicked
              : colors.links_default;
          }
        });
      })
      .on('mouseout', (event: MouseEvent, d: ExtendedINode) => {
        const currentNode = event.currentTarget as SVGElement;
        const parentGElement = currentNode.parentNode as SVGElement;
        const group = d3.select(parentGElement);
        group
          .select('circle')
          .attr(
            'fill',
            selectedNodeIds?.size && selectedNodeIds.has(d.id)
              ? colors.clicked
              : colors.nodes_default
          );
        group
          .select('rect')
          .attr(
            'fill',
            selectedNodeIds?.size && selectedNodeIds.has(d.id)
              ? colors.clicked
              : colors.box_default
          );
        setTooltip({
          visible: false,
          content: {type: '', link: null, node: null},
          x: 0,
          y: 0,
        });
        link.attr('stroke', (l: any) => {
          return selectedLinkIds?.size && selectedLinkIds.has(getLinkId(l))
            ? colors.clicked
            : colors.links_default;
        });
      })
      .on('click', (event: React.MouseEvent<HTMLElement>, d: ExtendedINode) => {
        event.stopPropagation();

        const newSetLinkIds: Set<string> = selectedLinkIds?.size
          ? new Set(selectedLinkIds)
          : new Set();
        const linkGraph = svg.append('g').selectAll('line').data(links).join('line');
        links.forEach((link: ExtendedILink) => {
          const sourceId =
            typeof link.source === 'object' ? link.source.id : link.source;
          const targetId =
            typeof link.target === 'object' ? link.target.id : link.target;
          if (sourceId === d.id || targetId === d.id) {
            const linkId = getLinkId(link);
            if (newSetLinkIds.has(linkId) && selectedNodeIds?.has(d.id)) {
              newSetLinkIds.delete(linkId);
              linkGraph.filter((l: ExtendedILink) => l && getLinkId(l) === linkId).attr('stroke', colors.clicked);
            } else {
              newSetLinkIds.add(linkId);
              linkGraph.filter((l: ExtendedILink) => l && getLinkId(l) === linkId).attr('stroke', colors.clicked);
            }
          }
        });
        onLinkSelectionChange &&
          onLinkSelectionChange(Array.from(newSetLinkIds));

        const newSetNodes: Set<number> = selectedNodeIds?.size
          ? new Set(selectedNodeIds)
          : new Set();
        if (newSetNodes.has(d.id)) {
          newSetNodes.delete(d.id);
          d3.select(event.currentTarget).attr('fill', colors.hover);
        } else {
          newSetNodes.add(d.id);
          d3.select(event.currentTarget).attr('fill', colors.clicked);
        }
        onNodeSelectionChange && onNodeSelectionChange(Array.from(newSetNodes));
      });

    //Add react component labels inside nodes
    nodeGroup
      .append('foreignObject')
      .attr('width', (d: any) => (d.name !== 'OTHERS' ? d.radius * 2 : 320))
      .attr('height', (d: any) => (d.name !== 'OTHERS' ? d.radius * 2 : 108))
      .attr('x', (d: any) => -d.radius)
      .attr('y', (d: any) => -d.radius)
      .attr('pointer-events', 'none')
      .each(function (d: any) {
        const container = document.createElement('div');
        container.className = 'center-text-box';
        this.appendChild(container);
        //create react root and render comp
        const root = createRoot(container);
        root.render(<InnerTextFrame item={d} />);
        nodeLabelRootsRef.current.set(d.id, root);
      });

    nodeGroup.each(function (d: any) {
      //check text and radius ratio if text is long than move text on top of the circle
      if (d.name.length / d.radius >= 0.2 && d.name !== 'OTHERS') {
        d3.select(this)
          .append('text')
          .text((d: any) => d.name)
          .classed('text_on_top', true) // always has base class
          .attr('x', 0)
          .attr('y', (d: any) => -d.radius - 5)
          .attr('data-testid', (d) => {
            return 'btn-result-text-' + d.index;
          })
          .each(function (d: any) {
            const el = d3.select(this);
            // assign size-specific class based on radius
            if (d.radius >= 50) el.classed('text_on_top', true);
            else if (d.radius > 40) el.classed('text_on_top_medium', true);
            else el.classed('text_on_top_small', true);
          });
      }
    });

    //Position the main node A at the center
    if (selectedNodeIds?.size === 0 && selectedLinkIds?.size === 0) {
      const mainNode = nodes.find((node: any) => node.id === 6);
      if (mainNode) {
        mainNode.fx = size.width / 2;
        mainNode.fy = size.height / 2;
      }
    }

    //update function moving lines with circles
    simulation.on('tick', () => {
      nodeGroup
        .attr('cx', (d: any) => {
          d.x = Math.max(
            MARGIN.left + d.radius,
            Math.min(size.width - MARGIN.right - d.radius, d.x)
          );
          return d.x;
        })
        .attr('cy', (d: any) => {
          d.y = Math.max(
            MARGIN.top + d.radius,
            Math.min(size.height - MARGIN.bottom - d.radius, d.y)
          );
          return d.y;
        });
      nodeGroup.attr('cx', (d: any) => d.x).attr('cy', (d: any) => d.y);
      link
        .attr('x1', (d: any) => d.source.x)
        .attr('y1', (d: any) => d.source.y)
        .attr('x2', (d: any) => d.target.x)
        .attr('y2', (d: any) => d.target.y);

      //update node group position (this moves circle and text both)
      nodeGroup.attr('transform', (d: any) => `translate(${d.x},${d.y})`);
    });

    // Drag behavior function
    function drag(simulation: any) {
      function dragStarted(event: any, d: any) {
        setTooltip((prev) => ({...prev, visible: false}));
        if (!event.active) simulation.alphaTarget(0.3).restart();
        d.fx = d.x;
        d.fy = d.y;
      }

      function dragged(event: any, d: any) {
        const mouseX = event.x;
        const mouseY = event.y;
        if (
          size &&
          mouseX > MARGIN.left + d.radius &&
          mouseX < size.width - MARGIN.right - d.radius &&
          mouseY > MARGIN.top + d.radius &&
          mouseY < size.height - MARGIN.bottom - d.radius
        ) {
          d.fx = mouseX;
          d.fy = mouseY;
        }
      }

      function dragEnded(event: any) {
        setTooltip((prev) => ({
          ...prev,
          visible: true,
          x: event.x,
          y: event.y,
        }));
        if (!event.active) simulation.alphaTarget(0);
        simulation.alpha(0.5).restart();
      }

      return d3
        .drag()
        .on('start', dragStarted)
        .on('drag', dragged)
        .on('end', dragEnded);
    }
  }, [size?.width, size?.height, selectedNodeIds, selectedLinkIds, data]);

  return (
    <Box className="bubbles-container">
      <svg
        ref={svgRef}
        width={size?.width}
        height={size?.height}
        data-testid={`link-and-bubble-svg`} />
      {tooltip.visible && (
        <div className="tooltip-container" data-testid={`link-and-bubble-div`}>
          <ArrTooltip
            defaultOpen={tooltip.visible}
            content={
              tooltip.content.type === 'link' ? (
                <LinkTooltip item={tooltip.content.link} />
              ) : (
                <BubbleTooltip item={tooltip.content.node} />
              )
            }
            //@ts-expect-error-- type mismatch
            PopperProps={{
              anchorEl: {
                getBoundingClientRect: () => ({
                  top: tooltip.y,
                  left: tooltip.x,
                  right: tooltip.x,
                  bottom: tooltip.y,
                  width: 0,
                  height: 0,
                }),
              },
            }}
          />
        </div>
      )}
    </Box>
  );
};

export default LinkAndBubble;
